import { injectIntl, IntlShape } from 'react-intl';
import {
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
} from '@material-ui/core';
import { useEffect, useMemo, useState } from 'react';
import { get, isEmpty, isNil, isNull, uniq, without } from 'lodash';
import { Create } from '@material-ui/icons';
import { Listings } from 'containers/AppContainer/typings';
import SahamAccordion from 'components/SahamAccordion';
import messages from 'containers/CourtManagerLegalProceedings/Provision/Regulation/messages';
import { translate } from 'containers/services/translate';
import { enableRemainingJudgement } from 'containers/CourtManagerLegalProceedings/Provision/Regulation/Recap/utils';
import {
  REGULATIONS,
  REGULATION_AMOUNTS,
} from 'containers/CourtManagerLegalProceedings/Provision/Regulation/data-structure';
import {
  isMinor,
  numberWithCommas,
  resolveValue,
} from 'containers/CourtManagerLegalProceedings/Provision/Regulation/utils';
import { formatPourcentage } from 'containers/services/formatNumberInput';
import { INJURY_POST_DECEASED } from 'containers/PreOpeningPage/constants';
import DialogFees from 'containers/CourtManagerLegalProceedings/Provision/Regulation/RiskAmount/DialogFees';
import Dialog from 'containers/CourtManagerLegalProceedings/Provision/Regulation/RiskAmount/Dialog';
import { JUDICIARY_TYPE } from 'containers/CourtManagerLegalProceedings/Provision/Regulation/constants';
import {
  getExecutionByVictim,
  getTheLatestEvaluation,
  hasCompanyAmount,
  mapVictimAmounts,
  isLatestRegulationExistBawaba,
} from 'containers/CourtManagerLegalProceedings/Provision/Regulation/RiskAmount/utils';
import Beneficiary from 'containers/CourtManagerLegalProceedings/Provision/Regulation/RiskAmount/Beneficiary';
import { getDahirAmountByVictimKey } from 'containers/LawyerLegalProceedingsPage/JudgementOnMerits/utils';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import { VictimType } from 'typings/globalTypes';
import { formatVictimsToDahirAmount } from 'containers/LawyerLegalProceedingsPage/utils';

export interface RiskAmountProps {
  intl: IntlShape;
  setRiskRecap: Function;
  riskRecap: any;
  listings: Listings;
  judgementOnMeritsByVictims: any;
  judgementOnMeritsByVehicles: any;
  activeStep: any;
  rcCorporeal: any;
  rcMaterial: any;
  additionalWarrenties: any;
  setActiveStep: any;
  isStepValidated: any;
  setIsStepValidated: Function;
  requestGetDahirAmount: Function;
  requestGetDahirAmountCorpo: Function;
  sagisClaim: any;
  claimId: number;
  dahirAmount: any;
  dahirAmountsCorpo: any;
  isBasicManagementRegulation?: boolean;
  corpoRegulations: any;
}

export const RiskAmount: React.FC<RiskAmountProps> = ({
  listings,
  rcMaterial,
  rcCorporeal,
  riskRecap,
  additionalWarrenties,
  setRiskRecap,
  sagisClaim,
  ...props
}): JSX.Element => {
  const victimAmountsMapper = useMemo(
    () => mapVictimAmounts(rcCorporeal.data),
    [rcCorporeal.data],
  );
  const [openDialogValidation, setOpenDialogValidation] = useState({} as any);
  const [openDialogFeesValidation, setOpenDialogFeesValidation] = useState(
    {} as any,
  );

  const hasRemainingAmount = enableRemainingJudgement(
    get(rcCorporeal, 'regulationInfo.natureCode'),
  );

  const dahirAmountByVictimKey = getDahirAmountByVictimKey(
    [],
    without(
      uniq(
        get(rcCorporeal, 'data', [])
          .filter((v: any) => v['injuryPostKey'] === INJURY_POST_DECEASED)
          .map((v: any) => v.victimKey),
      ),
      null,
      '',
    ),
    props.dahirAmount,
  );

  useEffect(() => {
    props.setIsStepValidated({
      ...props.isStepValidated,
      [props.activeStep]:
        !get(rcCorporeal, 'data', []).every(
          (item: any) => get(item, 'regulationAmount.executionRate') !== '',
        ) || !hasCompanyAmount(rcCorporeal, get(sagisClaim, 'lossDate')),
    });
  }, [
    get(rcCorporeal, 'data', []).every(
      (item: any) => get(item, 'regulationAmount.executionRate') !== '',
    ),
    hasCompanyAmount(rcCorporeal, get(sagisClaim, 'lossDate')),
  ]);
  const handleDialogValidation = (type: any, id: any) => () => {
    setOpenDialogValidation({
      [id]: {
        activateAppeal:
          type === JUDICIARY_TYPE.APPEAL
            ? !get(openDialogValidation[id], 'activateAppeal', false)
            : get(openDialogValidation[id], 'activateAppeal', false),
        activateJudgement:
          type === JUDICIARY_TYPE.FIRST_INSTANCE
            ? !get(openDialogValidation[id], 'activateJudgement', false)
            : get(openDialogValidation[id], 'activateJudgement', false),
        type,
      },
    });
  };
  const handleDialogFeesValidation = (type: string, id: any) => () => {
    setOpenDialogFeesValidation({
      [id]: {
        [REGULATION_AMOUNTS.MEDICAL_FEES]:
          type === REGULATION_AMOUNTS.MEDICAL_FEES
            ? !get(openDialogFeesValidation[id], 'medicalFees', false)
            : get(openDialogFeesValidation[id], 'medicalFees', false),
        [REGULATION_AMOUNTS.FUNERAL_FEES]:
          type === REGULATION_AMOUNTS.FUNERAL_FEES
            ? !get(openDialogFeesValidation[id], 'funeralFees', false)
            : get(openDialogFeesValidation[id], 'funeralFees', false),
        type: type,
      },
    });
  };
  const closeDialog = (victimId: number) => {
    setOpenDialogValidation({
      [victimId]: {
        ...openDialogValidation[victimId],
        activateAppeal: false,
        activateJudgement: false,
      },
    });
  };
  const closeDialogFees = (victimId: number) => {
    setOpenDialogFeesValidation({
      [victimId]: {
        ...openDialogValidation[victimId],
        [REGULATION_AMOUNTS.MEDICAL_FEES]: false,
        [REGULATION_AMOUNTS.FUNERAL_FEES]: false,
      },
    });
  };
  useEffect(() => {
    if (!isEmpty(rcCorporeal)) {
      props.requestGetDahirAmountCorpo(props.claimId);
      props.requestGetDahirAmount(
        get(
          formatVictimsToDahirAmount(get(rcCorporeal, 'data', [])),
          'injured',
          [],
        ),
        get(
          formatVictimsToDahirAmount(get(rcCorporeal, 'data', [])),
          'deceased',
          [],
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleChangeExpanded = (
    event: React.ChangeEvent<{}>,
    risk: any,
    dataType: any,
  ) => {
    const risks = [...riskRecap] as any;
    const currentType =
      risks.find((type: any) => risk.riskName === type.riskName) || {};
    const currentData = get(currentType, 'data', []).find(
      (item: any) => item.id === dataType.id,
    );
    if (!isEmpty(currentType) && !isEmpty(currentData)) {
      const index = risks.indexOf(currentType);
      const indexData = get(currentType, 'data', []).indexOf(currentData);
      risks[index].data[indexData].regulationAmount.expanded = !get(
        currentData,
        'regulationAmount.expanded',
        false,
      );

      return setRiskRecap(risks);
    }
  };
  const handleChange = (risk: any, dataType: any, beneficiary?: any) => (
    event: React.ChangeEvent<{ name: string; value: any }>,
  ) => {
    const risks = [...riskRecap] as any;
    const currentType =
      risks.find((type: any) => risk.riskName === type.riskName) || {};
    const currentData = get(currentType, 'data', []).find(
      (item: any) => item.id === dataType.id,
    );
    if (!isEmpty(currentType) && !isEmpty(currentData)) {
      const index = risks.indexOf(currentType);
      const indexData = get(currentType, 'data', []).indexOf(currentData);
      const regulationAmount = get(
        risks[index].data[indexData],
        'regulationAmount',
        {},
      );
      switch (event.target.name) {
        case REGULATION_AMOUNTS.PRINCIPAL_AMOUNT:
          regulationAmount[event.target.name] = numberWithCommas(
            event.target.value,
          );
          break;
        case REGULATION_AMOUNTS.RATE:
          regulationAmount[event.target.name] = formatPourcentage(
            event.target.value,
          );
          break;
        case REGULATION_AMOUNTS.COMPANY_AMOUNT:
          regulationAmount[event.target.name] = numberWithCommas(
            event.target.value,
          );
          break;
        case REGULATION_AMOUNTS.EXECUTION_PROV_AMOUNT:
          regulationAmount[event.target.name] = numberWithCommas(
            event.target.value,
          );
          break;

        case REGULATION_AMOUNTS.COMPENSATION_AMOUNT:
          regulationAmount[event.target.name] = numberWithCommas(
            event.target.value,
          );
          break;

        case REGULATION_AMOUNTS.APPEAL_AMOUNT:
          regulationAmount[event.target.name] = numberWithCommas(
            event.target.value,
          );
          break;

        case REGULATION_AMOUNTS.MEDICAL_FEES:
          regulationAmount[event.target.name] = numberWithCommas(
            event.target.value,
          );
          break;

        case REGULATION_AMOUNTS.FUNERAL_FEES:
          regulationAmount[event.target.name] = numberWithCommas(
            event.target.value,
          );
          break;

        default:
          regulationAmount[event.target.name] = event.target.value;

          break;
      }
      return setRiskRecap(risks);
    }
  };
  return (
    <main>
      {!isEmpty(rcCorporeal) && (
        <div>
          <span className="data-info">
            {translate(messages.victimInformationTitle, props)}
          </span>

          {get(rcCorporeal, 'data', []).map((victim: VictimType) => (
            <>
              <SahamAccordion
                key={rcCorporeal.regulationId}
                title={`${victim.lastName} ${victim.firstName}`}
                onChange={_ => handleChangeExpanded(_, rcCorporeal, victim)}
                expanded={get(victim, 'regulationAmount.expanded', true)}
              >
                <Grid container spacing={4} className="regulation-info">
                  {victim?.injuryPostKey !== INJURY_POST_DECEASED ? (
                    <>
                      {get(
                        rcCorporeal.regulationInfo,
                        [REGULATIONS.IS_APPEAL],
                        '',
                      ) && (
                        <>
                          {props.isBasicManagementRegulation &&
                          isNil(
                            get(
                              victimAmountsMapper[victim.victimKey],
                              'appealAmount',
                              null,
                            ),
                          ) ? (
                            <>
                              {/* Montant indemnité d'arrêt */}
                              <Grid item xs={3}>
                                <FormControl fullWidth>
                                  <TextField
                                    fullWidth
                                    label={translate(
                                      messages.appealCompensationAmount,
                                      props,
                                    )}
                                    value={numberWithCommas(
                                      String(
                                        get(
                                          victim,
                                          'regulationAmount.appealAmount',
                                          '',
                                        ),
                                      ),
                                    )}
                                    error={isEmpty(
                                      get(
                                        victim,
                                        'regulationAmount.appealAmount',
                                        '',
                                      ),
                                    )}
                                    name={REGULATION_AMOUNTS.APPEAL_AMOUNT}
                                    id={REGULATION_AMOUNTS.APPEAL_AMOUNT}
                                    onChange={handleChange(rcCorporeal, victim)}
                                  />
                                </FormControl>
                              </Grid>
                            </>
                          ) : (
                            <Grid item xs={3}>
                              {/* Montant indemnité d'arrêt */}
                              <FormControl fullWidth>
                                <TextField
                                  fullWidth
                                  label={translate(
                                    messages.appealCompensationAmount,
                                    props,
                                  )}
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <IconButton
                                          aria-label="toggle password visibility"
                                          onClick={handleDialogValidation(
                                            JUDICIARY_TYPE.APPEAL,
                                            victim.id,
                                          )}
                                          edge="end"
                                        >
                                          {<Create />}
                                        </IconButton>
                                      </InputAdornment>
                                    ),
                                  }}
                                  value={numberWithCommas(
                                    String(
                                      get(
                                        victim.regulationAmount,
                                        REGULATION_AMOUNTS.APPEAL_AMOUNT,
                                        '',
                                      ),
                                    ),
                                  )}
                                  disabled={true}
                                />
                              </FormControl>
                            </Grid>
                          )}
                          {!isEmpty(get(victim, 'victimKey', '')) &&
                            !isEmpty(props.dahirAmount) && (
                              <Grid item xs={3}>
                                {/* Montant calculé de l'arrêt */}
                                <TextField
                                  fullWidth
                                  label={translate(
                                    messages.appealCalculatedAmount,
                                    props,
                                  )}
                                  value={getTheLatestEvaluation(
                                    get(victim, 'victimKey', ''),
                                    props.dahirAmount,
                                  )}
                                  disabled={true}
                                />
                              </Grid>
                            )}
                        </>
                      )}

                      <>
                        {props.isBasicManagementRegulation &&
                        isNil(
                          get(
                            victimAmountsMapper[victim.victimKey],
                            'compensationAmount',
                            null,
                          ),
                        ) ? (
                          <Grid item xs={3}>
                            <FormControl fullWidth>
                              <TextField
                                fullWidth
                                label={translate(
                                  messages.judgementCompensationAmount,
                                  props,
                                )}
                                value={numberWithCommas(
                                  String(
                                    get(
                                      victim,
                                      'regulationAmount.compensationAmount',
                                      '',
                                    ),
                                  ),
                                )}
                                error={isEmpty(
                                  get(
                                    victim,
                                    'regulationAmount.compensationAmount',
                                    '',
                                  ),
                                )}
                                name={REGULATION_AMOUNTS.COMPENSATION_AMOUNT}
                                id={REGULATION_AMOUNTS.COMPENSATION_AMOUNT}
                                onChange={handleChange(rcCorporeal, victim)}
                              />
                            </FormControl>
                          </Grid>
                        ) : (
                          <Grid item xs={3}>
                            {/* Montant indemnité du jugement */}
                            <TextField
                              fullWidth
                              label={translate(
                                messages.judgementCompensationAmount,
                                props,
                              )}
                              value={numberWithCommas(
                                String(
                                  get(
                                    victim,
                                    'regulationAmount.compensationAmount',
                                    '',
                                  ),
                                ),
                              )}
                              InputProps={{
                                endAdornment: (
                                  <>
                                    <InputAdornment position="end">
                                      <IconButton
                                        id={'update-icon'}
                                        name={'update-icon'}
                                        onClick={handleDialogValidation(
                                          JUDICIARY_TYPE.FIRST_INSTANCE,
                                          victim.id,
                                        )}
                                        edge="end"
                                      >
                                        <Create />
                                      </IconButton>
                                    </InputAdornment>
                                  </>
                                ),
                              }}
                              disabled={true}
                            />
                          </Grid>
                        )}
                        {!isEmpty(get(victim, 'victimKey', '')) &&
                          !isEmpty(props?.dahirAmount) &&
                          !get(
                            rcCorporeal?.regulationInfo,
                            [REGULATIONS.IS_APPEAL],
                            '',
                          ) && (
                            <Grid item xs={3}>
                              <TextField
                                fullWidth
                                label={translate(
                                  messages.judgementCalculatedAmount,
                                  props,
                                )}
                                value={getTheLatestEvaluation(
                                  get(victim, 'victimKey', ''),
                                  props.dahirAmount,
                                )}
                                disabled={true}
                              />
                            </Grid>
                          )}
                      </>

                      {isMinor(
                        victim,
                        { victim },
                        rcCorporeal,
                        get(
                          rcCorporeal.regulationInfo,
                          REGULATIONS.REGULATION_DATE,
                          [],
                        ),
                      ) && (
                        <Grid item xs={3}>
                          <FormControl fullWidth>
                            <TextField
                              fullWidth
                              label={translate(
                                messages.judgementCompanyAmount,
                                props,
                              )}
                              value={numberWithCommas(
                                String(
                                  get(
                                    victim,
                                    'regulationAmount.companyAmount',
                                    '',
                                  ),
                                ),
                              )}
                              error={isEmpty(
                                get(
                                  victim,
                                  'regulationAmount.companyAmount',
                                  '',
                                ),
                              )}
                              name={REGULATION_AMOUNTS.COMPANY_AMOUNT}
                              id={REGULATION_AMOUNTS.COMPANY_AMOUNT}
                              onChange={handleChange(rcCorporeal, victim)}
                            />
                          </FormControl>
                        </Grid>
                      )}
                    </>
                  ) : (
                    <>
                      {featureService.hasFeature(
                        FEATURES.JUDGEMENT_ON_MERITS_INFO,
                      ) && (
                        <>
                          {(!isNull(
                            get(
                              victim.regulationAmount,
                              REGULATION_AMOUNTS.MEDICAL_FEES,
                              '',
                            ),
                          ) ||
                            props.isBasicManagementRegulation) && (
                            <Grid item xs={4}>
                              <FormControl fullWidth>
                                <TextField
                                  fullWidth
                                  label={translate(
                                    messages.deceasedMedicalFees,
                                    props,
                                  )}
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <IconButton
                                          aria-label="toggle password visibility"
                                          onClick={handleDialogFeesValidation(
                                            REGULATION_AMOUNTS.MEDICAL_FEES,
                                            victim.id,
                                          )}
                                          id={REGULATION_AMOUNTS.MEDICAL_FEES}
                                          name={REGULATION_AMOUNTS.MEDICAL_FEES}
                                          edge="end"
                                        >
                                          {<Create />}
                                        </IconButton>
                                      </InputAdornment>
                                    ),
                                  }}
                                  value={numberWithCommas(
                                    String(
                                      get(
                                        victim.regulationAmount,
                                        REGULATION_AMOUNTS.MEDICAL_FEES,
                                        '',
                                      ),
                                    ),
                                  )}
                                  disabled={true}
                                />
                              </FormControl>
                            </Grid>
                          )}
                          {(!isNull(
                            get(
                              victim.regulationAmount,
                              REGULATION_AMOUNTS.FUNERAL_FEES,
                              '',
                            ),
                          ) ||
                            props.isBasicManagementRegulation) && (
                            <Grid item xs={4}>
                              <FormControl fullWidth>
                                <TextField
                                  fullWidth
                                  label={translate(
                                    messages.deceasedFuneralFees,
                                    props,
                                  )}
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <IconButton
                                          aria-label="toggle password visibility"
                                          onClick={handleDialogFeesValidation(
                                            REGULATION_AMOUNTS.FUNERAL_FEES,
                                            victim.id,
                                          )}
                                          edge="end"
                                        >
                                          {<Create />}
                                        </IconButton>
                                      </InputAdornment>
                                    ),
                                  }}
                                  value={numberWithCommas(
                                    String(
                                      get(
                                        victim.regulationAmount,
                                        REGULATION_AMOUNTS.FUNERAL_FEES,
                                        '',
                                      ),
                                    ),
                                  )}
                                  disabled={true}
                                />
                              </FormControl>
                            </Grid>
                          )}
                        </>
                      )}
                    </>
                  )}
                  <Grid item xs={3}>
                    <FormControl fullWidth>
                      <TextField
                        fullWidth
                        label={translate(messages.rate, props)}
                        onChange={handleChange(rcCorporeal, victim)}
                        name={REGULATION_AMOUNTS.RATE}
                        id={REGULATION_AMOUNTS.RATE}
                        type="number"
                        InputProps={{
                          inputProps: { min: 0, max: 100 },
                          endAdornment: (
                            <InputAdornment position="end">
                              {translate(messages.ratePercentage, props)}
                            </InputAdornment>
                          ),
                        }}
                        value={get(
                          victim,
                          'regulationAmount.executionRate',
                          '',
                        )}
                        error={
                          get(victim, 'regulationAmount.executionRate', '') ===
                          ''
                        }
                        disabled={get(
                          victim,
                          'regulationAmount.isTotalExecution',
                          false,
                        )}
                      />
                    </FormControl>
                  </Grid>
                  {hasRemainingAmount &&
                    isEmpty(get(victim, 'beneficiaries', [])) && (
                      <Grid item xs={3}>
                        <FormControl fullWidth>
                          <TextField
                            fullWidth
                            label={translate(
                              messages.provisionalExecution,
                              props,
                            )}
                            disabled={isLatestRegulationExistBawaba(
                              props.corpoRegulations,
                              get(sagisClaim, 'regulations'),
                              get(listings, 'PaymentStatus', []),
                              victim,
                            )}
                            name={REGULATION_AMOUNTS.EXECUTION_PROV_AMOUNT}
                            id={REGULATION_AMOUNTS.EXECUTION_PROV_AMOUNT}
                            value={get(
                              victim,
                              'regulationAmount.manualExecutionProvAmount',
                              resolveValue(
                                get(
                                  getExecutionByVictim(
                                    get(sagisClaim, 'regulations'),
                                    victim,
                                    get(listings, 'PaymentStatus', []),
                                  ),
                                  'executionProvAmount',
                                ),
                              ),
                            )}
                            onChange={handleChange(rcCorporeal, victim)}
                          />
                        </FormControl>
                      </Grid>
                    )}
                  {get(victim, 'beneficiaries', []).map((beneficiary: any) => {
                    return (
                      <Beneficiary
                        openDialogValidation={openDialogValidation}
                        beneficiary={beneficiary}
                        setRiskRecap={setRiskRecap}
                        riskRecap={riskRecap}
                        corpoRegulations={props.corpoRegulations}
                        rcCorporeal={rcCorporeal}
                        closeDialog={closeDialog}
                        handleDialogValidation={handleDialogValidation}
                        dahirAmountByVictimKey={dahirAmountByVictimKey}
                        victim={victim}
                        regulations={get(sagisClaim, 'regulations', [])}
                        listings={listings}
                        isBasicManagementRegulation={
                          props.isBasicManagementRegulation
                        }
                      />
                    );
                  })}
                </Grid>
              </SahamAccordion>
              <DialogFees
                open={
                  get(openDialogFeesValidation[victim?.id as any], [
                    REGULATION_AMOUNTS.MEDICAL_FEES,
                  ]) ||
                  get(openDialogFeesValidation[victim?.id as any], [
                    REGULATION_AMOUNTS.FUNERAL_FEES,
                  ])
                }
                handleClose={() => closeDialogFees(victim.id as any)}
                handleValidate={() => closeDialogFees(victim.id as any)}
                item={victim}
                handleChange={handleChange}
                feeType={get(
                  openDialogFeesValidation[victim?.id as any],
                  'type',
                )}
                title={translate(messages.updateDialogFeesTitle, props, {
                  type:
                    get(openDialogFeesValidation[victim?.id as any], 'type') ===
                    REGULATION_AMOUNTS.MEDICAL_FEES
                      ? translate(messages.deceasedMedicalFees, props)
                      : translate(messages.deceasedFuneralFees, props),
                })}
                rcCorporeal={rcCorporeal}
                disabled={
                  props.isBasicManagementRegulation
                    ? false
                    : get(
                        openDialogFeesValidation[victim?.id as any],
                        'type',
                      ) === REGULATION_AMOUNTS.MEDICAL_FEES
                    ? isEmpty(
                        get(
                          victim.regulationAmount,
                          REGULATION_AMOUNTS.MEDICAL_FEES_REASON,
                          '',
                        ),
                      )
                    : isEmpty(
                        get(
                          victim.regulationAmount,
                          REGULATION_AMOUNTS.FUNERAL_FEES_REASON,
                          '',
                        ),
                      )
                }
                isBasicManagementRegulation={props.isBasicManagementRegulation}
              />
              <Dialog
                open={
                  get(
                    openDialogValidation[victim?.id as any],
                    'activateJudgement',
                    false,
                  ) ||
                  get(
                    openDialogValidation[victim?.id as any],
                    'activateAppeal',
                    false,
                  )
                }
                handleClose={() => closeDialog(victim.id as any)}
                handleValidate={() => closeDialog(victim.id as any)}
                item={victim}
                riskRecap={riskRecap}
                setRiskRecap={setRiskRecap}
                openDialogValidation={openDialogValidation}
                handleChange={handleChange}
                rcCorporeal={rcCorporeal}
                disabled={
                  props.isBasicManagementRegulation &&
                  isEmpty(get(victim, 'regulationAmount.appealReason', '')) &&
                  isEmpty(get(victim, 'regulationAmount.judgementReason', ''))
                    ? false
                    : get(openDialogValidation[victim?.id as any], 'type') ===
                      JUDICIARY_TYPE.APPEAL
                    ? isEmpty(get(victim, 'regulationAmount.appealReason', ''))
                    : isEmpty(
                        get(victim, 'regulationAmount.judgementReason', ''),
                      )
                }
                title={translate(messages.updateVictimDialogTitle, props, {
                  type:
                    get(openDialogValidation[victim?.id as any], 'type') ===
                    JUDICIARY_TYPE.APPEAL
                      ? translate(messages.appeal, props)
                      : translate(messages.judgement, props),
                })}
                isBasicManagementRegulation={props.isBasicManagementRegulation}
              />
            </>
          ))}
        </div>
      )}
      {!isEmpty(rcMaterial) && (
        <div>
          <span className="data-info">
            {translate(messages.vehicleInformationTitle, props)}
          </span>
          {get(rcMaterial, 'data', []).map((vehicle: any) => (
            <SahamAccordion
              key={rcCorporeal.regulationId}
              title={vehicle.registrationNumber}
              onChange={_ => handleChangeExpanded(_, rcMaterial, vehicle)}
              expanded={get(vehicle, 'regulationAmount.expanded', true)}
            >
              <Grid container spacing={4} className="regulation-info">
                {get(
                  rcMaterial.regulationInfo,
                  [REGULATIONS.IS_APPEAL],
                  '',
                ) && (
                  <Grid item xs={3}>
                    <FormControl fullWidth>
                      <TextField
                        fullWidth
                        onChange={handleChange(rcMaterial, vehicle)}
                        label={translate(messages.appealDamageAmount, props)}
                        disabled={true}
                        value={numberWithCommas(
                          String(
                            get(
                              vehicle.regulationAmount,
                              REGULATION_AMOUNTS.APPEAL_DAMAGE_AMOUNT,
                              '',
                            ),
                          ),
                        )}
                      />
                    </FormControl>
                  </Grid>
                )}
                {!isNull(
                  get(
                    vehicle.regulationAmount,
                    [REGULATION_AMOUNTS.DAMAGE_AMOUNT],
                    null,
                  ),
                ) && (
                  <Grid item xs={3}>
                    <FormControl fullWidth>
                      <TextField
                        fullWidth
                        onChange={handleChange(rcMaterial, vehicle)}
                        label={translate(messages.judgementDamageAmount, props)}
                        disabled={true}
                        value={numberWithCommas(
                          String(
                            get(
                              vehicle.regulationAmount,
                              REGULATION_AMOUNTS.DAMAGE_AMOUNT,
                              '',
                            ),
                          ),
                        )}
                      />
                    </FormControl>
                  </Grid>
                )}
                <Grid item xs={3}>
                  <FormControl fullWidth>
                    <TextField
                      fullWidth
                      onChange={handleChange(rcMaterial, vehicle)}
                      label={translate(messages.principalAmount, props)}
                      name={REGULATION_AMOUNTS.PRINCIPAL_AMOUNT}
                      id={REGULATION_AMOUNTS.PRINCIPAL_AMOUNT}
                      value={get(
                        vehicle,
                        'regulationAmount.principalAmount',
                        '',
                      )}
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </SahamAccordion>
          ))}
        </div>
      )}
      {!isEmpty(additionalWarrenties) && (
        <div>
          <span className="data-info">
            {translate(messages.additionalGarentiesTitle, props)}
          </span>
          {get(additionalWarrenties, 'data', []).map((item: any) => (
            <SahamAccordion
              key={additionalWarrenties.regulationId}
              title={item.id}
              onChange={_ =>
                handleChangeExpanded(_, additionalWarrenties, item)
              }
              expanded={get(item, 'regulationAmount.expanded', true)}
            >
              <Grid container spacing={4} className="regulation-info">
                <Grid item xs={4}>
                  <FormControl fullWidth>
                    <TextField
                      fullWidth
                      onChange={handleChange(additionalWarrenties, item)}
                      label={translate(messages.principalAmount, props)}
                      data-testid={REGULATION_AMOUNTS.PRINCIPAL_AMOUNT}
                      name={REGULATION_AMOUNTS.PRINCIPAL_AMOUNT}
                      value={get(item, 'regulationAmount.principalAmount', '')}
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </SahamAccordion>
          ))}
        </div>
      )}
    </main>
  );
};

export default injectIntl(RiskAmount);
